import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { OrderLanguage } from "../../utils/Languages";
import MainComponentWrapper from "../Common/MainComponentWrapper";
import EventSection from "./EventSection";
import GalleryModal from "../Common/Modal/GalleryModal";
import {
  dateFormatter,
  getAttendeePhotoUrl,
  getEventImageUrl,
} from "../../utils/Helper";

const DEFAULT_AVATAR = "/event/assets/img/user.png";

const normalizeGalleryList = (gallery) => {
  if (!gallery) {
    return [];
  }
  const list = Array.isArray(gallery) ? gallery : [gallery];
  return list
    .map((item) => (Array.isArray(item) ? item[0] : item))
    .filter(Boolean);
};

const TicketDetails = ({ isLoading, isError, details }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const { orderId } = useParams();
  const attendee = details?.attendee;
  const event = details?.event;
  const photoUrl = getAttendeePhotoUrl(attendee?.photo, DEFAULT_AVATAR);
  const hasPhoto =
    Boolean(attendee?.photo) &&
    photoUrl !== DEFAULT_AVATAR &&
    !avatarFailed;
  const profileSrc = hasPhoto ? photoUrl : DEFAULT_AVATAR;

  const galleryImages = useMemo(() => {
    const fromEvent = normalizeGalleryList(event?.gallery);
    const fromEventDoc = normalizeGalleryList(details?.eventId?.gallery);
    return fromEvent.length ? fromEvent : fromEventDoc;
  }, [event?.gallery, details?.eventId?.gallery]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [photoUrl]);

  return (
    <MainComponentWrapper Loading={isLoading} errorInfo={isError}>
      <div className="event-ticket-detail-body">
        <div className="event-ticket-print-area divToPrint">
          <EventSection
            eventDetials={{
              ...event,
              gallery: galleryImages.length ? galleryImages : event?.gallery,
            }}
            loading={isLoading}
            error={isError}
            details={details}
            attendeeDetail={attendee}
            orderId={orderId}
            showDownload={true}
            topClassName="event-ticket-detail-top"
            eventCardClassName="event-ticket-detail-event-card"
          />

          {galleryImages.length > 0 && (
            <section
              className="event-ticket-detail-gallery"
              aria-label="Event gallery"
            >
              <h3 className="event-order-detail-section-title">Gallery</h3>
              <div className="event-ticket-detail-gallery-grid">
                {galleryImages.map((image, index) => {
                  const src = getEventImageUrl(image, "");
                  if (!src) {
                    return null;
                  }
                  return (
                    <button
                      key={image?._id || image?.path || image?.filename || index}
                      type="button"
                      className="event-ticket-detail-gallery-item"
                      aria-label={`Open gallery image ${index + 1}`}
                      onClick={() => {
                        setGalleryIndex(index);
                        setGalleryOpen(true);
                      }}
                    >
                      <img data-pdf-role="gallery" src={src} alt="" />
                    </button>
                  );
                })}
              </div>
              <GalleryModal
                filesData={galleryImages}
                Open={galleryOpen}
                Index={galleryIndex}
                onClose={() => setGalleryOpen(false)}
              />
            </section>
          )}

          <section
            className="event-ticket-detail-pass"
            aria-label="Ticket holder information"
          >
            <div className="event-ticket-detail-pass-inner">
              <img
                className="event-ticket-detail-profile"
                data-pdf-role="profile"
                src={profileSrc}
                alt={attendee?.name || "Attendee"}
                onClick={() => hasPhoto && setIsOpen(true)}
                onError={() => setAvatarFailed(true)}
                style={{ cursor: hasPhoto ? "pointer" : "default" }}
              />
              {hasPhoto && (
                <GalleryModal
                  filesData={
                    Array.isArray(attendee.photo)
                      ? attendee.photo
                      : [attendee.photo]
                  }
                  Open={isOpen}
                  Index="0"
                  onClose={() => setIsOpen(false)}
                />
              )}

              <div className="event-ticket-detail-pass-copy">
                <h2 className="event-ticket-detail-pass-name">
                  {attendee?.name || "—"}
                </h2>
                <dl className="event-ticket-detail-pass-meta">
                  <div className="event-ticket-detail-pass-row">
                    <dt>
                      {
                        OrderLanguage.OrderDetails.billingSection.detialsColumn
                          .email
                      }
                    </dt>
                    <dd>{attendee?.email || "—"}</dd>
                  </div>
                  <div className="event-ticket-detail-pass-row">
                    <dt>
                      {
                        OrderLanguage.OrderDetails.billingSection.detialsColumn
                          .phone
                      }
                    </dt>
                    <dd>{attendee?.mobile || "—"}</dd>
                  </div>
                  <div className="event-ticket-detail-pass-row">
                    <dt>
                      {
                        OrderLanguage.OrderDetails.billingSection.detialsColumn
                          .date
                      }
                    </dt>
                    <dd>{dateFormatter(event?.startDate)}</dd>
                  </div>
                  <div className="event-ticket-detail-pass-row">
                    <dt>
                      {
                        OrderLanguage.OrderDetails.billingSection.detialsColumn
                          .dob
                      }
                    </dt>
                    <dd>{dateFormatter(attendee?.dob)}</dd>
                  </div>
                </dl>
              </div>

              <div className="event-ticket-detail-qr-wrap">
                <QRCode
                  className="event-ticket-detail-qr"
                  value={`${process.env.REACT_APP_BASE_URL}/guard/attendees/${attendee?._id}?varify=true`}
                  size={148}
                />
              </div>
            </div>
          </section>

          {event?.instrunction && (
            <section
              className="event-ticket-detail-instructions"
              aria-label="Event instructions"
            >
              <h3 className="event-order-detail-section-title">Instructions</h3>
              <ol className="event-ticket-detail-instructions-list">
                {event.instrunction.split(",").map((item, index) => (
                  <li key={index}>{item.trim()}</li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </div>
    </MainComponentWrapper>
  );
};

export default TicketDetails;
